/**
 * UUID v4 generator using crypto.getRandomValues()
 * @returns A UUID v4 string
 */
const generateUUIDWithCryptoGetRandomValues = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);

  // Set version (4) and variant bits
  array[6] = (array[6] & 0x0f) | 0x40;
  array[8] = (array[8] & 0x3f) | 0x80;

  // Convert to hex string with dashes
  const hex = Array.from(array, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
};

/**
 * UUID v4 generator using Math.random()
 * @returns A UUID v4 string
 */
const generateUUIDWithMathRandom = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * @returns A UUID v4 string using the best available method
 */
export const generateUUID = (): string => {
  // Try crypto.randomUUID() first (most secure)
  if (typeof crypto !== 'undefined' && crypto.randomUUID !== undefined) {
    return crypto.randomUUID();
  }

  // Try crypto.getRandomValues() as fallback (still cryptographically secure)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues !== undefined) {
    return generateUUIDWithCryptoGetRandomValues();
  }

  // Fall back to Math.random() (least secure but always available)
  return generateUUIDWithMathRandom();
};

/**
 * Retrieves or generates a fabric session ID for cross-domain tracking
 * Stores the ID in sessionStorage for persistence across page reloads
 * @returns A fabric session ID string
 */
export const getFabricSessionId = (): string => {
  try {
    const existingId = sessionStorage.getItem('fabric_sid');
    if (existingId) return existingId;

    const newId = generateUUID();
    sessionStorage.setItem('fabric_sid', newId);

    return newId;
  } catch {
    // If sessionStorage is not available, generate a new ID to avoid breaking tracking
    return generateUUID();
  }
};
